import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Skeleton from "@mui/material/Skeleton";
import {getQuotaTotals} from "../../api/tenantDetailApi";
import type {Quotas, QuotaType, TenantQuota} from "../../types/tenant";
import {SectionCard} from "../ui/SectionCard";
import {colors} from "../../theme/tokens";
import {formatCount, formatStorageBytes} from "../../util/format";
import {QuotaEditDialogContent, QuotaUsageDialogContent, quotaTypeLabelKey} from "./QuotaDialogs";

const QUOTA_ORDER: QuotaType[] = ["sms", "storageBytes", "email"];
const WARNING_RATIO = 0.8;

type QuotasSectionProps = {
    tenantId: string;
    number: number;
    id: string;
    /** Called after a quota change was saved, e.g. to refresh tenant data elsewhere on the page. */
    onChanged?: () => void;
};

export function QuotasSection({tenantId, number, id, onChanged}: QuotasSectionProps) {
    const {t} = useTranslation();
    const [totals, setTotals] = useState<Quotas | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [usageOpen, setUsageOpen] = useState(false);
    const latestRequest = useRef(0);

    const load = useCallback(() => {
        const request = ++latestRequest.current;
        getQuotaTotals(tenantId)
                .then((loaded) => {
                    if (request !== latestRequest.current) return;
                    setTotals(loaded);
                    setError(null);
                })
                .catch((e: unknown) => {
                    if (request !== latestRequest.current) return;
                    setError(e instanceof Error ? e.message : "");
                });
    }, [tenantId]);

    useEffect(load, [load]);

    return (
            <SectionCard
                    id={id}
                    number={number}
                    title={t("DETAIL_QUOTAS")}
                    actions={
                        <>
                            <Button variant="outlined" onClick={() => setEditOpen(true)}>{t("DETAIL_EDIT_QUOTAS")}</Button>
                            <Button variant="outlined" onClick={() => setUsageOpen(true)}>{t("DETAIL_QUOTA_USAGE_REPORT")}</Button>
                        </>
                    }
            >
                {error !== null && (
                        <Alert severity="error">
                            {error ? `${t("DETAIL_QUOTA_TOTALS_ERROR")}: ${error}` : t("DETAIL_QUOTA_TOTALS_ERROR")}
                        </Alert>
                )}
                {error === null && (
                        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"}, gap: 3}}>
                            {QUOTA_ORDER.map((quota) =>
                                    totals ? (
                                            <QuotaMeter key={quota} label={t(quotaTypeLabelKey(quota))} quota={totals[quota]} storage={quota === "storageBytes"} />
                                    ) : (
                                            <Skeleton key={quota} height={36} />
                                    ),
                            )}
                        </Box>
                )}

                <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                    <QuotaEditDialogContent
                            tenantId={tenantId}
                            onClose={() => setEditOpen(false)}
                            onSaved={() => {
                                setEditOpen(false);
                                load();
                                onChanged?.();
                            }}
                    />
                </Dialog>
                <Dialog open={usageOpen} onClose={() => setUsageOpen(false)} maxWidth="md" fullWidth>
                    <QuotaUsageDialogContent tenantId={tenantId} onClose={() => setUsageOpen(false)} />
                </Dialog>
            </SectionCard>
    );
}

function QuotaMeter({label, quota, storage}: {label: string; quota: TenantQuota; storage: boolean}) {
    const format = storage ? formatStorageBytes : formatCount;
    const ratio = quota.max > 0 ? quota.used / quota.max : quota.used > 0 ? 1 : 0;
    const barColor = ratio >= 1 ? colors.error : ratio >= WARNING_RATIO ? colors.expiring : colors.primary;
    return (
            <Box>
                <Box sx={{display: "flex", justifyContent: "space-between", fontSize: "12.5px", mb: 0.75}}>
                    <Box component="span" sx={{color: colors.muted}}>{label}</Box>
                    <Box component="span" sx={{color: colors.ink, fontVariantNumeric: "tabular-nums"}}>
                        {format(quota.used)} / {format(quota.max)}
                    </Box>
                </Box>
                <Box
                        role="meter"
                        aria-label={label}
                        aria-valuemin={0}
                        aria-valuemax={quota.max}
                        aria-valuenow={Math.min(quota.used, quota.max)}
                        sx={{height: 6, borderRadius: 3, backgroundColor: colors.tableHeader, overflow: "hidden"}}
                >
                    <Box sx={{height: "100%", width: `${Math.min(ratio, 1) * 100}%`, backgroundColor: barColor}} />
                </Box>
            </Box>
    );
}
